import { inputTypes } from "data/globalData";
import { channelTypes } from "components/PimDashboard/DashboardComponents/Buying/BuyingSheet/data";
import { trimCategoryDropdownsObj } from "components/PimDashboard/DashboardComponents/Sourcing/TrimsMaster/data";
import {
	TRIM_BREAKDOWN_PRESERVE_KEYS,
	isBreakdownRowComplete as isCatalogBreakdownRowComplete,
	isCatalogDrivenTrimCategory,
} from "./trimCatalogHelpers";

export const styleFabricAssociation = "styleFabric";
export const styleTrimsAssociation = "styleTrims";
export const styleValueAddAssocition = "styleValueAdd";
export const styleFabricItemsAssociationObj = "styleFabricDetailItems";
export const styleTrimsItemsAssociationObj = "styleTrimsDetailItems";
export const styleValueAddItemsAssociationObj = "styleValueAddDetailItems";
export const pimTrimBreakdownAssociationObj = "styleTrimsItemsBreakdown";
export const styleTrimsBreakdownAssociationObj = "styleTrimsItemsBreakdown";
export const trimsBreakDownLegacyKey = "trimsBreakDown";
export const valueAddConsumptionAssociationObj = "valueAddItemConsumption";
export const valueAddConsumptionPayloadKey = "valueAddItemConsumption";
export const valueAddConsumptionLegacyKey = "valueAddConsumption";
export const pimDescCatAssociationObj = "CategorySubCategories";

export const normalizeStyleFabricItems = (fabricTrimsData) => {
	const fabric = fabricTrimsData?.[styleFabricAssociation];
	if (Array.isArray(fabric)) return fabric;
	return fabric?.[styleFabricItemsAssociationObj] ?? [];
};

export const getTrimItemBreakdownRows = (item) =>
	item?.[pimTrimBreakdownAssociationObj] ?? item?.[styleTrimsBreakdownAssociationObj] ?? item?.[trimsBreakDownLegacyKey] ?? [];

export const getValueAddItemConsumptionRows = (item) =>
	item?.[valueAddConsumptionAssociationObj] ?? item?.[valueAddConsumptionLegacyKey] ?? [];

export const normalizeStyleTrimsItems = (fabricTrimsData) => {
	const trims = fabricTrimsData?.[styleTrimsAssociation];
	if (Array.isArray(trims)) return trims;
	return (trims?.[styleTrimsItemsAssociationObj] ?? []).map((item) => ({
		...item,
		[pimTrimBreakdownAssociationObj]: normalizeTrimBreakdownRowsForGrid(getTrimItemBreakdownRows(item), { prefix: "trim-bd" }),
	}));
};

export const normalizeStyleValueAddItems = (fabricTrimsData) => {
	const valueAdd = fabricTrimsData?.[styleValueAddAssocition];
	if (Array.isArray(valueAdd)) return valueAdd;
	return (valueAdd?.[styleValueAddItemsAssociationObj] ?? []).map((item) => ({
		...item,
		[valueAddConsumptionAssociationObj]: normalizeValueAddBreakdownRowsForGrid(getValueAddItemConsumptionRows(item), {
			prefix: "va-bd",
		}),
	}));
};

export const sectionDetailsColumns = {
	is_completed: { name: "Completed", key: "is_completed" },
	completed_by: { name: "Completed By", key: "completed_by" },
};
export const sectionCompletedStatus = "YES";

export const fabricDetailsCompleteApi = "/api/fabric_trims/complete_fabric_details";
export const trimsDetailsCompleteApi = "/api/fabric_trims/complete_trims_details";
export const valueAddDetailsCompleteApi = "/api/fabric_trims/complete_value_add_details";

export const isSectionMarkedComplete = (sectionDetails) =>
	sectionDetails?.[sectionDetailsColumns.is_completed.key] === sectionCompletedStatus;

export const fabricTrimsPublishSections = [
	{ label: "Fabric", associationKey: styleFabricAssociation },
	{ label: "Accessories", associationKey: styleTrimsAssociation },
	{ label: "Value Addition", associationKey: styleValueAddAssocition },
];

export const trimsTableColumns = {
	item_category: { name: "Category", key: "item_category", type: inputTypes.SELECT.key, defaultValue: inputTypes.SELECT.key },
	item_sub_category: {
		name: "Sub Category",
		key: "item_sub_category",
		type: inputTypes.SELECT.key,
		defaultValue: inputTypes.SELECT.key,
	},
	color_level: { name: "Color Wise", key: "color_level", type: inputTypes.ENTER.key, defaultValue: false },
	size_level: { name: "Size Wise", key: "size_level", type: inputTypes.ENTER.key, defaultValue: false },
	channel_level: { name: "Channel Wise", key: "channel_level", type: inputTypes.ENTER.key, defaultValue: false },
};
export const trimBreakdownTableColumns = {
	item_category: { name: "Category", key: "item_category" },
	item_sub_category: { name: " Sub Category", key: "item_sub_category" },
	option_id: { name: "Option Id", key: "option_id" },
	size: { name: "Size", key: "size" },
	channel: { name: "Channel", key: "channel" },
	trim_code: { name: "Trim Code", key: "trim_code", type: inputTypes.SELECT.key, defaultValue: inputTypes.SELECT.key },
	description: { name: "Description", key: "item_description" },
};
export const reqColorLevelColsForTrimConsump = ["color", "pallete", "option_id"];
export const reqColorLevelColsForTrimBreakdown = reqColorLevelColsForTrimConsump;

export const breakdownGridRowIdKey = "gridRowId";
export const breakdownRowUpdatedKey = "is_updated";

export const buildBreakdownPayloadRows = (rows = [], { isNewParent = false, breakdownOnly = false } = {}) => {
	const mapped = (rows ?? []).map((row) => {
		const {
			id: bdId,
			[breakdownGridRowIdKey]: _gridRowId,
			[breakdownRowUpdatedKey]: isUpdated,
			...bdRest
		} = row ?? {};

		if (isNewParent) {
			return bdRest;
		}

		const payload = { id: bdId, ...bdRest };
		if (isUpdated === true) {
			payload[breakdownRowUpdatedKey] = true;
		}
		return payload;
	});

	if (isNewParent || !breakdownOnly) {
		return mapped;
	}

	return mapped.filter((row) => row?.[breakdownRowUpdatedKey] === true);
};

export const trimBreakdownKeys = {
	category: trimsTableColumns.item_category.key,
	subCategory: trimsTableColumns.item_sub_category.key,
	colorLevel: trimsTableColumns.color_level.key,
	sizeLevel: trimsTableColumns.size_level.key,
	channelLevel: trimsTableColumns.channel_level.key,
};

export const buildBreakdownGridRowId = ({ prefix = "row", color = "", size = "", channel = "", optionId = "", index = 0 }) =>
	`${prefix}-${optionId || "all"}-${color || "all"}-${size || "all"}-${channel || "all"}-${index}`;

export const normalizeBreakdownColorRow = (colorRow) => {
	if (!colorRow || typeof colorRow !== "object") return {};
	return {
		color: colorRow?.color ?? "",
		pallete: colorRow?.pallete ?? "",
		option_id: colorRow?.option_id ?? "",
	};
};

export const getBreakdownChannelKeys = () => Object.keys(channelTypes);

export const ensureBreakdownGridRowIds = (rows = [], { prefix = "row" } = {}) =>
	rows.map((row, index) => ({
		...row,
		[breakdownGridRowIdKey]: buildBreakdownGridRowId({
			prefix,
			color: row?.color,
			size: row?.size,
			channel: row?.channel,
			optionId: row?.option_id,
			index,
		}),
	}));

export const isTruthyBreakdownLevel = (value) => value === true || value === 1 || value === "1";

export const repairDuplicateBreakdownRowIds = (rows = []) => {
	const seenIds = new Set();
	let nextLocalId = Date.now();

	return rows.map((row) => {
		if (row?.id == null || seenIds.has(row.id)) {
			return { ...row, id: ++nextLocalId };
		}
		seenIds.add(row.id);
		return row;
	});
};

export const normalizeTrimBreakdownRowsForGrid = (rows = [], { prefix = "trim-bd" } = {}) =>
	repairDuplicateBreakdownRowIds(ensureBreakdownGridRowIds(rows, { prefix }));

export const getExpectedBreakdownRowCount = ({ trimObj, colorRows = [], sizeRows = [] }) => {
	if (!trimObj) return 0;

	const hasColorLevel = isTruthyBreakdownLevel(trimObj?.[trimBreakdownKeys.colorLevel]);
	const hasSizeLevel = isTruthyBreakdownLevel(trimObj?.[trimBreakdownKeys.sizeLevel]);
	const hasChannelLevel = isTruthyBreakdownLevel(trimObj?.[trimBreakdownKeys.channelLevel]);
	const colorCount = hasColorLevel ? Math.max(colorRows.length, 1) : 1;
	const sizeCount = hasSizeLevel ? Math.max(sizeRows.length, 1) : 1;
	const channelCount = hasChannelLevel ? getBreakdownChannelKeys().length : 1;

	return colorCount * sizeCount * channelCount;
};

export const matchBreakdownRowKey = (row) =>
	`${row?.option_id ?? ""}|${row?.color ?? ""}|${row?.size ?? ""}|${row?.channel ?? ""}`;

export const reconcileTrimBreakdownRows = ({ trimObj, colorRows = [], sizeRows = [], existingRows = [] }) => {
	if (!trimObj) return [];

	const expectedCount = getExpectedBreakdownRowCount({ trimObj, colorRows, sizeRows });
	const normalizedExisting = normalizeTrimBreakdownRowsForGrid(existingRows, { prefix: "trim-bd" });

	if (normalizedExisting.length === expectedCount) {
		return normalizedExisting.map((row) => ({
			...row,
			color: row.color ?? "ALL",
			pallete: row.pallete ?? "",
			option_id: row.option_id ?? "",
			[trimBreakdownTableColumns.size.key]: row[trimBreakdownTableColumns.size.key] ?? "ALL",
			[trimBreakdownTableColumns.channel.key]: row[trimBreakdownTableColumns.channel.key] ?? "ALL",
		}));
	}

	const existingByKey = new Map(normalizedExisting.map((row) => [matchBreakdownRowKey(row), row]));
	const freshRows = buildTrimBreakdownRows({ trimObj, colorRows, sizeRows });

	return normalizeTrimBreakdownRowsForGrid(
		freshRows.map((row) => {
			const existing = existingByKey.get(matchBreakdownRowKey(row));
			const withLevels = {
				...row,
				color: row.color ?? "ALL",
				pallete: row.pallete ?? "",
				option_id: row.option_id ?? "",
				[trimBreakdownTableColumns.size.key]: row[trimBreakdownTableColumns.size.key] ?? "ALL",
				[trimBreakdownTableColumns.channel.key]: row[trimBreakdownTableColumns.channel.key] ?? "ALL",
			};
			if (!existing) return withLevels;

			return {
				...withLevels,
				id: existing.id,
				[trimBreakdownTableColumns.trim_code.key]:
					existing[trimBreakdownTableColumns.trim_code.key] ?? withLevels[trimBreakdownTableColumns.trim_code.key],
				item_description:
					existing.item_description ??
					withLevels.item_description ??
					existing.description ??
					withLevels.description ??
					"",
				url: existing.url ?? withLevels.url,
				last_updated_by: existing.last_updated_by,
				...TRIM_BREAKDOWN_PRESERVE_KEYS.reduce((acc, key) => {
					if (existing[key] !== undefined) acc[key] = existing[key];
					return acc;
				}, {}),
			};
		}),
		{ prefix: "trim-bd" },
	);
};

export const isFilledSelectValue = (value) => Boolean(value && value !== inputTypes.SELECT.key);

export const trimItemHasBreakdown = (item) =>
	getTrimItemBreakdownRows(item).length > 0 ||
	isCatalogDrivenTrimCategory(item?.item_category) ||
	isTruthyBreakdownLevel(item?.[trimBreakdownKeys.colorLevel]) ||
	isTruthyBreakdownLevel(item?.[trimBreakdownKeys.sizeLevel]) ||
	isTruthyBreakdownLevel(item?.[trimBreakdownKeys.channelLevel]);

export const getTrimsCompleteValidationError = (trimsArr = []) => {
	for (const trimItem of trimsArr) {
		if (!trimItemHasBreakdown(trimItem)) continue;
		const breakdownRows = getTrimItemBreakdownRows(trimItem);
		const invalidRow = breakdownRows.find(
			(row) => !isCatalogBreakdownRowComplete(row, trimItem?.item_category),
		);
		if (invalidRow) {
			const label = [trimItem?.item_category, trimItem?.item_sub_category].filter(Boolean).join(" - ");
			if (trimItem?.item_category === trimCategoryDropdownsObj?.ZIPPER?.key) {
				return `Complete zipper details for all breakdown rows in ${label || "accessory item"}`;
			}
			if (trimItem?.item_category === trimCategoryDropdownsObj?.THREAD?.key) {
				return `Complete thread details for all breakdown rows in ${label || "accessory item"}`;
			}
			return `Select Trim Code for all breakdown rows in ${label || "accessory item"}`;
		}
	}
	return "";
};

export const normalizeTrimBreakdownSizeData = (rawSizes) => {
	if (Array.isArray(rawSizes)) {
		return rawSizes
			.map((row) => {
				if (typeof row === "string" || typeof row === "number") return String(row);
				return row?.name ?? row?.key ?? row?.size ?? row?.value ?? "";
			})
			.filter(Boolean);
	}
	if (typeof rawSizes === "string") {
		return rawSizes
			.split(",")
			.map((row) => row.trim())
			.filter(Boolean);
	}
	return [];
};

export const buildTrimBreakdownRows = ({ trimObj, colorRows = [], sizeRows = [] }) => {
	if (!trimObj) return [];

	const hasColorLevel = isTruthyBreakdownLevel(trimObj?.[trimBreakdownKeys.colorLevel]);
	const hasSizeLevel = isTruthyBreakdownLevel(trimObj?.[trimBreakdownKeys.sizeLevel]);
	const hasChannelLevel = isTruthyBreakdownLevel(trimObj?.[trimBreakdownKeys.channelLevel]);
	const nextColorRows = hasColorLevel
		? (colorRows.length ? colorRows.map(normalizeBreakdownColorRow) : [{ color: "ALL", pallete: "", option_id: "" }])
		: [{ color: "ALL", pallete: "", option_id: "" }];
	const nextSizeRows = hasSizeLevel ? (sizeRows.length ? sizeRows : ["ALL"]) : ["ALL"];
	const nextChannelRows = hasChannelLevel ? getBreakdownChannelKeys() : ["ALL"];

	const baseRow = {
		[trimBreakdownTableColumns.item_category.key]: trimObj?.[trimBreakdownKeys.category] ?? "",
		[trimBreakdownTableColumns.item_sub_category.key]: trimObj?.[trimBreakdownKeys.subCategory] ?? "",
		[trimBreakdownTableColumns.trim_code.key]: trimObj?.[trimBreakdownTableColumns.trim_code.key] ?? "",
		[trimBreakdownTableColumns.description.key]: trimObj?.[trimBreakdownTableColumns.description.key] ?? "",
	};

	let id = new Date().valueOf();
	let rowIndex = 0;
	const rows = [];
	nextColorRows.forEach((colorRow) => {
		nextSizeRows.forEach((sizeRow) => {
			nextChannelRows.forEach((channelRow) => {
				const rowData = {
					...baseRow,
					...(hasColorLevel ? colorRow : { color: "ALL", pallete: "", option_id: "" }),
					[trimBreakdownTableColumns.size.key]: hasSizeLevel ? sizeRow : "ALL",
					[trimBreakdownTableColumns.channel.key]: hasChannelLevel ? channelRow : "ALL",
					id: ++id,
				};
				rows.push({
					...rowData,
					[breakdownGridRowIdKey]: buildBreakdownGridRowId({
						prefix: "trim-bd",
						color: rowData?.color,
						size: rowData?.size,
						channel: rowData?.channel,
						optionId: rowData?.option_id,
						index: rowIndex++,
					}),
				});
			});
		});
	});

	return rows;
};

export const normalizeValueAddBreakdownRowsForGrid = (rows = [], { prefix = "va-bd" } = {}) =>
	repairDuplicateBreakdownRowIds(ensureBreakdownGridRowIds(rows, { prefix }));

export const valueAddTableColumns = {
	category: { name: "Category", key: "category", type: inputTypes.SELECT.key, defaultValue: inputTypes.SELECT.key },
	description: { name: "Description", key: "description", type: inputTypes.SELECT.key, defaultValue: inputTypes.SELECT.key },
	color_level: { name: "Color Wise", key: "color_level", type: inputTypes.ENTER.key, defaultValue: false },
	size_level: { name: "Size Wise", key: "size_level", type: inputTypes.ENTER.key, defaultValue: false },
	channel_level: { name: "Channel Wise", key: "channel_level", type: inputTypes.ENTER.key, defaultValue: false },
};
export const valueAddConsumptionTableColumns = {
	category: { name: "Category", key: "category" },
	description: { name: "Description", key: "description" },
	placement: { name: "Placement", key: "placement", type: inputTypes.SELECT.key, defaultValue: inputTypes.SELECT.key },
	color: { name: "Color", key: "color", type: inputTypes.ENTER.key, defaultValue: "" },
	pallete: { name: "Pallete", key: "pallete", type: inputTypes.ENTER.key, defaultValue: "" },
	size: { name: "Size", key: "size", type: inputTypes.ENTER.key, defaultValue: "" },
	channel: { name: "Channel", key: "channel", type: inputTypes.ENTER.key, defaultValue: "" },
};

export const reqColorLevelColsForValueAddBreakdown = ["color", "pallete"];

export const valueAddBreakdownKeys = {
	category: valueAddTableColumns.category.key,
	description: valueAddTableColumns.description.key,
	colorLevel: valueAddTableColumns.color_level.key,
	sizeLevel: valueAddTableColumns.size_level.key,
	channelLevel: valueAddTableColumns.channel_level.key,
};

export const valueAddItemHasBreakdown = (item) =>
	isTruthyBreakdownLevel(item?.[valueAddBreakdownKeys.colorLevel]) ||
	isTruthyBreakdownLevel(item?.[valueAddBreakdownKeys.sizeLevel]) ||
	isTruthyBreakdownLevel(item?.[valueAddBreakdownKeys.channelLevel]);

export const getValueAddCompleteValidationError = (valueAddArr = []) => {
	for (const valueAddItem of valueAddArr) {
		if (!valueAddItemHasBreakdown(valueAddItem)) continue;
		const consumptionRows = getValueAddItemConsumptionRows(valueAddItem);
		const invalidRow = consumptionRows.find(
			(row) => !isFilledSelectValue(row?.[valueAddConsumptionTableColumns.placement.key]),
		);
		if (invalidRow) {
			const label = [valueAddItem?.category, valueAddItem?.description].filter(Boolean).join(" - ");
			return `Select Placement for all breakdown rows in ${label || "value add item"}`;
		}
	}
	return "";
};

export const buildValueAddConsumptionRows = ({ valueAddObj, colorRows = [], sizeRows = [] }) => {
	if (!valueAddObj) return [];

	const hasColorLevel = isTruthyBreakdownLevel(valueAddObj?.[valueAddBreakdownKeys.colorLevel]);
	const hasSizeLevel = isTruthyBreakdownLevel(valueAddObj?.[valueAddBreakdownKeys.sizeLevel]);
	const hasChannelLevel = isTruthyBreakdownLevel(valueAddObj?.[valueAddBreakdownKeys.channelLevel]);
	const nextColorRows = hasColorLevel
		? (colorRows.length ? colorRows.map(normalizeBreakdownColorRow) : [{ color: "ALL", pallete: "", option_id: "" }])
		: [{ color: "ALL", pallete: "", option_id: "" }];
	const nextSizeRows = hasSizeLevel ? (sizeRows.length ? sizeRows : ["ALL"]) : ["ALL"];
	const nextChannelRows = hasChannelLevel ? getBreakdownChannelKeys() : ["ALL"];

	const baseRow = {
		[valueAddConsumptionTableColumns.category.key]: valueAddObj?.[valueAddBreakdownKeys.category] ?? "",
		[valueAddConsumptionTableColumns.description.key]: valueAddObj?.[valueAddBreakdownKeys.description] ?? "",
		[valueAddConsumptionTableColumns.placement.key]: inputTypes.SELECT.key,
	};

	let id = new Date().valueOf();
	let rowIndex = 0;
	const rows = [];
	nextColorRows.forEach((colorRow) => {
		nextSizeRows.forEach((sizeRow) => {
			nextChannelRows.forEach((channelRow) => {
				const rowData = {
					...baseRow,
					...(hasColorLevel ? colorRow : {}),
					...(hasSizeLevel ? { [valueAddConsumptionTableColumns.size.key]: sizeRow } : {}),
					...(hasChannelLevel ? { [valueAddConsumptionTableColumns.channel.key]: channelRow } : {}),
					id: ++id,
				};
				rows.push({
					...rowData,
					[breakdownGridRowIdKey]: buildBreakdownGridRowId({
						prefix: "va-bd",
						color: rowData?.color,
						size: rowData?.size,
						channel: rowData?.channel,
						optionId: rowData?.option_id,
						index: rowIndex++,
					}),
				});
			});
		});
	});

	return rows;
};

export const reconcileValueAddBreakdownRows = ({ valueAddObj, colorRows = [], sizeRows = [], existingRows = [] }) => {
	const hasBreakdown =
		isTruthyBreakdownLevel(valueAddObj?.[valueAddBreakdownKeys.colorLevel]) ||
		isTruthyBreakdownLevel(valueAddObj?.[valueAddBreakdownKeys.sizeLevel]) ||
		isTruthyBreakdownLevel(valueAddObj?.[valueAddBreakdownKeys.channelLevel]);

	if (!hasBreakdown) return [];

	const expectedCount = getExpectedBreakdownRowCount({ trimObj: valueAddObj, colorRows, sizeRows });
	const normalizedExisting = normalizeValueAddBreakdownRowsForGrid(existingRows, { prefix: "va-bd" });

	if (normalizedExisting.length === expectedCount) {
		return normalizedExisting;
	}

	const existingByKey = new Map(normalizedExisting.map((row) => [matchBreakdownRowKey(row), row]));
	const freshRows = buildValueAddConsumptionRows({ valueAddObj, colorRows, sizeRows });

	return normalizeValueAddBreakdownRowsForGrid(
		freshRows.map((row) => {
			const existing = existingByKey.get(matchBreakdownRowKey(row));
			if (!existing) return row;

			return {
				...row,
				id: existing.id,
				[valueAddConsumptionTableColumns.placement.key]:
					existing[valueAddConsumptionTableColumns.placement.key] ?? row[valueAddConsumptionTableColumns.placement.key],
				updated_by: existing.updated_by,
			};
		}),
		{ prefix: "va-bd" },
	);
};

export const trimsRequiredForValues = {
	SEWING: { name: "SEWING", key: "SEWING" },
	PACKING: { name: "PACKING", key: "PACKING" },
};
export const valueAddSourcingValues = {
	PURCHASE: { name: "PURCHASE", key: "PURCHASE" },
	IMPORT: { name: "IMPORT", key: "IMPORT" },
	BUYING_ORDER: { name: "BUYING ORDER", key: "BUYING_ORDER" },
};
export const valueAddRequiredForValues = {
	SEWING: { name: "SEWING", key: "SEWING" },
	PACKING: { name: "PACKING", key: "PACKING" },
};
export const trimsSourcingValues = {
	PURCHASE: { name: "PURCHASE", key: "PURCHASE" },
	IMPORT: { name: "IMPORT", key: "IMPORT" },
	BUYING_ORDER: { name: "BUYING ORDER", key: "BUYING_ORDER" },
};
export const pimConsumpGarmentOptions = {
	ALL: { name: "ALL", key: "ALL" },
	PENDING: { name: "PENDING", key: "PENDING" },
};
